/* assets/gsap-init.js */
(() => {
  // ---- Guards: wait for GSAP, DOM ready ----
  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  // Toggle markers quickly via dev console: localStorage.setItem('ST_MARKERS','1')
  const SHOW_MARKERS = !!localStorage.getItem('ST_MARKERS');

  onReady(() => {
    if (!window.gsap || !window.ScrollTrigger) return;
    const { gsap } = window;
    const { ScrollTrigger, ScrollToPlugin } = window;

    // Safe register (no error if already registered)
    try { gsap.registerPlugin(ScrollTrigger, ScrollToPlugin); } catch (e) {}

    // Respect reduced motion
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Avoid fighting Dawn's built-in reveal setting: if it's on, we skip our reveal
    const DAWN_REVEAL_ENABLED = !!document.querySelector('script[src*="animations.js"]');

    // ---- Dedup helpers ----
    const boundReveals = new WeakSet();
    const boundParallax = new WeakSet();
    const boundVideos  = new WeakSet();
    const boundMagnet  = new WeakSet();
    const boundHorz    = new WeakSet();

    // ---- UTIL: (Re)bind everything in the current DOM ----
    function bindReveals(root = document) {
      if (prefersReduced || DAWN_REVEAL_ENABLED) return; // skip our reveal if user prefers less motion or Dawn's reveal is on
      root.querySelectorAll('[data-animate]').forEach((el) => {
        if (boundReveals.has(el)) return;
        boundReveals.add(el);

        const type = el.dataset.animate; // "fade-up" | "fade-in" | "slide-right"
        let fromVars = { opacity: 0, y: 32 };
        if (type === 'fade-in') fromVars = { opacity: 0 };
        if (type === 'slide-right') fromVars = { opacity: 0, x: -32 };

        gsap.from(el, {
          ...fromVars,
          duration: 0.9,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: el,
            start: 'top 85%',
            toggleActions: 'play none none none',
            once: true,
            markers: SHOW_MARKERS
          }
        });
      });
    }

    function bindParallax(root = document) {
      root.querySelectorAll('[data-parallax]').forEach((layer) => {
        if (boundParallax.has(layer)) return;
        boundParallax.add(layer);

        const speed = parseFloat(layer.dataset.speed || '0.2'); // can be negative
        gsap.to(layer, {
          yPercent: () => speed * 100,
          ease: 'none',
          scrollTrigger: {
            trigger: layer.closest('[data-parallax-wrap]') || layer,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
            markers: SHOW_MARKERS
          }
        });
      });
    }

    function bindHorizontalGalleries(root = document) {
      root.querySelectorAll('[data-horz]').forEach((wrap) => {
        if (boundHorz.has(wrap)) return;
        boundHorz.add(wrap);

        const track = wrap.querySelector('[data-horz-track]');
        if (!track) return;

        const mm = gsap.matchMedia();
        mm.add('(min-width: 768px)', () => {
          const panels = gsap.utils.toArray(track.children);
          const getTotal = () => panels.reduce((w, p) => w + p.offsetWidth, 0);
          const getEndX = () => Math.min(0, wrap.clientWidth - getTotal());
          const getEndScroll = () => Math.max(0, getTotal() - wrap.clientWidth);

          // Kill previous instance if re-entering
          let tl = wrap.__horzTL;
          if (tl) { tl.kill(); }

          tl = gsap.timeline({
            defaults: { ease: 'none' },
            scrollTrigger: {
              trigger: wrap,
              pin: true,
              scrub: 1,
              anticipatePin: 1,
              invalidateOnRefresh: true,
              end: () => '+=' + getEndScroll(),
              markers: SHOW_MARKERS
            }
          });
          wrap.__horzTL = tl;

          tl.to(track, { x: () => getEndX() });

          // Play/pause videos only when visible (horizontal awareness)
          panels.forEach((p) => {
            const vid = p.querySelector('video');
            if (!vid || boundVideos.has(vid)) return;
            boundVideos.add(vid);

            ScrollTrigger.create({
              trigger: p,
              start: 'left center',
              end: 'right center',
              horizontal: true,
              onEnter: () => { vid.currentTime = 0; vid.play().catch(()=>{}); },
              onEnterBack: () => { vid.currentTime = 0; vid.play().catch(()=>{}); },
              onLeave: () => vid.pause(),
              onLeaveBack: () => vid.pause(),
              markers: SHOW_MARKERS
            });
          });

          // Refresh on images load (responsive widths)
          const imgs = track.querySelectorAll('img');
          imgs.forEach(img => {
            if (img.complete) return;
            img.addEventListener('load', () => ScrollTrigger.refresh(), { once: true });
          });

          return () => {
            // cleanup when media query turns off
            if (wrap.__horzTL) { wrap.__horzTL.kill(); wrap.__horzTL = null; }
          };
        });
      });
    }

    function bindMagnetic(root = document) {
      root.querySelectorAll('[data-magnetic]').forEach((btn) => {
        if (boundMagnet.has(btn)) return;
        boundMagnet.add(btn);

        const strength = parseFloat(btn.dataset.magnet || '24');
        const move = (e) => {
          const r = btn.getBoundingClientRect();
          const x = e.clientX - (r.left + r.width / 2);
          const y = e.clientY - (r.top + r.height / 2);
          gsap.to(btn, { x: x / strength, y: y / strength, duration: 0.2 });
        };
        const reset = () => gsap.to(btn, { x: 0, y: 0, duration: 0.3, ease: 'power2.out' });

        btn.addEventListener('mousemove', move);
        btn.addEventListener('mouseleave', reset);
      });
    }

    // Bind everything in one go
    function bindAll(root = document) {
      bindReveals(root);
      bindParallax(root);
      bindHorizontalGalleries(root);
      bindMagnetic(root);
      // Refresh after binding to measure triggers correctly
      ScrollTrigger.refresh();
    }

    // Initial bind
    bindAll(document);

    // ---- Shopify Editor Support ----
    if (window.Shopify && Shopify.designMode) {
      const rebinder = (e) => {
        // Rebind only inside the changed section if possible
        const container = e?.target || document;
        bindAll(container);
      };
      document.addEventListener('shopify:section:load', rebinder);
      document.addEventListener('shopify:section:unload', () => ScrollTrigger.refresh());
      document.addEventListener('shopify:section:select', rebinder);
      document.addEventListener('shopify:section:deselect', () => ScrollTrigger.refresh());
      document.addEventListener('shopify:block:select', rebinder);
      document.addEventListener('shopify:block:deselect', () => ScrollTrigger.refresh());
      window.addEventListener('load', () => ScrollTrigger.refresh());
    } else {
      // Live site: refresh on full load (images/fonts settled)
      window.addEventListener('load', () => ScrollTrigger.refresh());
    }
  });
})();
